import React from 'react'

import { Modal } from '@runtime/components/ui/Modal'
import { useI18n } from '@runtime/core/i18n/i18n'
import { useAppStore } from '@runtime/core/store/appStore'
import { useUser } from '@runtime/core/user/userContext'
import PosterPrivateAccess from '@runtime/features/poster/components/PosterPrivateAccess'
import PosterPrivatePrompt from '@runtime/features/poster/components/PosterPrivatePrompt'
import PosterThemeGrid from '@runtime/features/poster/components/PosterThemeGrid'
import { usePosterThemePicker } from '@runtime/features/poster/hooks/usePosterThemePicker'
import styles from '@runtime/features/poster/PosterModal.module.scss'

type PosterModalProps = {
  open: boolean
  onClose: () => void
}

export default function PosterModal({ open, onClose }: PosterModalProps) {
  const { user } = useUser()
  const { t } = useI18n()
  const activeId = useAppStore((s) => s.themeId)
  const setThemeId = useAppStore((s) => s.setThemeId)
  const {
    applying,
    closePrivatePrompt,
    handlePrivatePasswordChange,
    handlePrivateSubmit,
    isPrivateUnlocked,
    isPrivateVisible,
    list,
    loadedMap,
    loading,
    markLoaded,
    onPick,
    openPrivatePrompt,
    privateError,
    privatePassword,
    showPrivatePrompt,
    visibleList,
  } = usePosterThemePicker({
    open,
    onClose,
    t,
    user,
    setThemeId,
  })

  // Mimic the original vanilla modal behaviour: prevent background scroll + close on ESC.
  React.useEffect(() => {
    if (!open) return

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function onEsc(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose && onClose()
    }

    window.addEventListener('keydown', onEsc)

    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onEsc)
    }
  }, [open, onClose])

  return (
    <Modal
      open={open}
      onClose={onClose}
      overlayClassName={styles.overlay}
      contentClassName={styles.modalContent}
      contentProps={{ 'data-testid': 'poster-overlay' }}
      useBaseContentClass={false}
      durationMs={260}
    >
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label={t('common.close')}
        title={t('common.close')}
        disabled={applying}
      >
        ×
      </button>

      <header className={styles.header}>{t('posterModal.title')}</header>

      <section className={styles.body} aria-live="polite">
        <PosterThemeGrid
          t={t}
          activeId={activeId}
          list={list}
          visibleList={visibleList}
          loadedMap={loadedMap}
          loading={loading}
          applying={applying}
          isPrivateVisible={isPrivateVisible}
          markLoaded={markLoaded}
          onPick={onPick}
        />

        <PosterPrivateAccess
          t={t}
          isPrivateUnlocked={isPrivateUnlocked}
          onOpenPrivatePrompt={openPrivatePrompt}
        />
      </section>

      <PosterPrivatePrompt
        open={showPrivatePrompt}
        t={t}
        privatePassword={privatePassword}
        privateError={privateError}
        onClose={closePrivatePrompt}
        onPasswordChange={handlePrivatePasswordChange}
        onSubmit={handlePrivateSubmit}
      />
    </Modal>
  )
}
